import type { Identifier } from "ra-core";
import { useGetList } from "ra-core";

export interface Pipeline {
  id: number;
  key: string;
  name: string;
  position: number;
  active: boolean;
}

export interface PipelineStage {
  id: number;
  pipeline_id: number;
  key: string;
  name: string;
  position: number;
  max_days: number | null;
  exit_rule: string | null;
  is_won: boolean;
  is_lost: boolean;
  procedure_id: number | null;
}

export interface LostReason {
  id: number;
  key: string;
  name: string;
  pipeline_id: number | null;
  requires_note: boolean;
  position: number;
  active: boolean;
}

// The part of a deal the board needs to place and move it.
export interface DealSlot {
  id: Identifier;
  stage_id?: number | null;
  index: number;
}

export interface DealUpdate {
  id: Identifier;
  data: { index: number; stage_id?: number };
}

export const usePipelines = () =>
  useGetList<Pipeline>("pipelines", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
    filter: { active: true },
  });

// Every stage of every pipeline, in one cached query: the board, the deal
// form and the stage names on the show pages read from it.
export const usePipelineStages = () =>
  useGetList<PipelineStage>("pipeline_stages", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "position", order: "ASC" },
  });

export const useLostReasons = () =>
  useGetList<LostReason>("lost_reasons", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "position", order: "ASC" },
  });

export const stagesOf = (stages: PipelineStage[], pipelineId?: number | null) =>
  stages
    .filter((stage) => stage.pipeline_id === pipelineId)
    .sort((a, b) => a.position - b.position);

// Active reasons that apply to a pipeline: the shared ones and its own.
export const reasonsFor = (reasons: LostReason[], pipelineId?: number | null) =>
  reasons.filter(
    (reason) =>
      reason.active &&
      (reason.pipeline_id === null || reason.pipeline_id === pipelineId),
  );

// Deals in columns by stage, each column ordered by index. A deal whose
// stage is not in the list (none yet, or another pipeline) is left out.
export const groupDealsByStage = <T extends DealSlot>(
  deals: T[],
  stages: PipelineStage[],
): Record<number, T[]> => {
  const columns: Record<number, T[]> = Object.fromEntries(
    stages.map((stage) => [stage.id, [] as T[]]),
  );
  deals.forEach((deal) => {
    if (deal.stage_id != null && columns[deal.stage_id]) {
      columns[deal.stage_id] = [...columns[deal.stage_id], deal];
    }
  });
  return Object.fromEntries(
    Object.entries(columns).map(([stageId, column]) => [
      stageId,
      [...column].sort((a, b) => a.index - b.index),
    ]),
  );
};

// The columns after moving a deal before another one (null: at the end),
// with the two columns involved renumbered from 0. Returns new arrays.
export const moveDealInColumns = <T extends DealSlot>(
  columns: Record<number, T[]>,
  dealId: Identifier,
  toStageId: number,
  beforeId: Identifier | null,
): Record<number, T[]> => {
  const fromStageId = Object.keys(columns)
    .map(Number)
    .find((stageId) => columns[stageId].some((deal) => deal.id === dealId));
  if (fromStageId === undefined) return columns;
  const deal = columns[fromStageId].find((item) => item.id === dealId)!;

  const source = columns[fromStageId].filter((item) => item.id !== dealId);
  const target =
    fromStageId === toStageId
      ? source
      : (columns[toStageId] ?? []).filter((item) => item.id !== dealId);
  const found =
    beforeId === null ? -1 : target.findIndex((item) => item.id === beforeId);
  const position = found === -1 ? target.length : found;
  const moved = { ...deal, stage_id: toStageId };
  const renumber = (column: T[]) =>
    column.map((item, index) => ({ ...item, index }));

  return {
    ...columns,
    [fromStageId]: renumber(source),
    [toStageId]: renumber([
      ...target.slice(0, position),
      moved,
      ...target.slice(position),
    ]),
  };
};

// The writes that turn the deals as they are into the columns after a
// move: only what changed, the moved deal first.
export const dealUpdates = <T extends DealSlot>(
  before: T[],
  after: Record<number, T[]>,
  movedId: Identifier,
): DealUpdate[] => {
  const previous = new Map(before.map((deal) => [deal.id, deal]));
  const updates = Object.values(after)
    .flat()
    .flatMap((deal): DealUpdate[] => {
      const old = previous.get(deal.id);
      const stageChanged = old?.stage_id !== deal.stage_id;
      if (!stageChanged && old?.index === deal.index && deal.id !== movedId) {
        return [];
      }
      return [
        {
          id: deal.id,
          data: {
            index: deal.index,
            ...(stageChanged && deal.stage_id != null
              ? { stage_id: deal.stage_id }
              : {}),
          },
        },
      ];
    });
  return [
    ...updates.filter((update) => update.id === movedId),
    ...updates.filter((update) => update.id !== movedId),
  ];
};

// Total of a stage in euro, or null when no deal of it has an amount: the
// coach, athlete and club deals usually have none, and "0 €" says nothing
export const stageAmount = (deals: { amount?: number | null }[]) =>
  deals.some((deal) => (deal.amount ?? 0) > 0)
    ? deals.reduce((sum, deal) => sum + (deal.amount ?? 0), 0)
    : null;

// Won and lost stages show their cards a page at a time (the count is
// always the real one)
export const CLOSED_STAGE_PAGE = 20;
